import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import type { Mesh } from "@babylonjs/core/Meshes/mesh";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import type { Scene } from "@babylonjs/core/scene";

interface PooledParticle {
  readonly mesh: Mesh;
  readonly velocity: Vector3;
  remaining: number;
}

export class ParticleFactory {
  private readonly particles: PooledParticle[] = [];
  private readonly wallMaterial: StandardMaterial;
  private readonly bloodMaterial: StandardMaterial;

  public constructor(scene: Scene) {
    this.wallMaterial = this.createMaterial(
      "wall-impact-particle-material",
      new Color3(1, 0.35, 0.12),
      scene,
    );
    this.bloodMaterial = this.createMaterial(
      "blood-impact-particle-material",
      new Color3(0.55, 0.01, 0.01),
      scene,
    );
    for (let index = 0; index < 36; index += 1) {
      const mesh = MeshBuilder.CreateBox(`impact-particle-${index}`, { size: 0.055 }, scene);
      mesh.material = this.wallMaterial;
      mesh.setEnabled(false);
      this.particles.push({ mesh, velocity: Vector3.Zero(), remaining: 0 });
    }
  }

  public burst(position: Vector3, direction: Vector3, blood: boolean): void {
    let emitted = 0;
    for (const particle of this.particles) {
      if (particle.remaining > 0) continue;
      particle.mesh.position.copyFrom(position);
      particle.mesh.scaling.setAll(blood ? 1.3 : 1);
      particle.mesh.material = blood ? this.bloodMaterial : this.wallMaterial;
      particle.velocity.copyFrom(direction).scaleInPlace(-1.2);
      particle.velocity.x += (Math.random() - 0.5) * 1.8;
      particle.velocity.y += Math.random() * 1.6;
      particle.velocity.z += (Math.random() - 0.5) * 1.8;
      particle.remaining = 0.25 + Math.random() * 0.2;
      particle.mesh.setEnabled(true);
      emitted += 1;
      if (emitted >= 7) break;
    }
  }

  public update(deltaSeconds: number): void {
    for (const particle of this.particles) {
      if (particle.remaining <= 0) continue;
      particle.remaining -= deltaSeconds;
      particle.velocity.y -= 5 * deltaSeconds;
      particle.mesh.position.x += particle.velocity.x * deltaSeconds;
      particle.mesh.position.y += particle.velocity.y * deltaSeconds;
      particle.mesh.position.z += particle.velocity.z * deltaSeconds;
      if (particle.remaining <= 0) particle.mesh.setEnabled(false);
    }
  }

  private createMaterial(name: string, color: Color3, scene: Scene): StandardMaterial {
    const material = new StandardMaterial(name, scene);
    material.emissiveColor = color;
    material.diffuseColor = Color3.Black();
    return material;
  }
}
